import React, { useEffect, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { questions, highestClarityMessages, lowestClarityMessages, whatsappMessages, WHATSAPP_NUMBER } from './data';
type Screen = 'entry' | 'questions' | 'results';
export function PropositoPage() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('entry');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>(new Array(3).fill(-1));
  // Persist state
  useEffect(() => {
    const savedState = sessionStorage.getItem('propositoState');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      if (parsed.answers) setAnswers(parsed.answers);
      if (parsed.currentScreen) setCurrentScreen(parsed.currentScreen);
      if (parsed.currentQuestionIndex !== undefined) setCurrentQuestionIndex(parsed.currentQuestionIndex);
    }
  }, []);
  useEffect(() => {
    sessionStorage.setItem('propositoState', JSON.stringify({
      answers,
      currentScreen,
      currentQuestionIndex
    }));
  }, [answers, currentScreen, currentQuestionIndex]);
  // Meta tags
  useEffect(() => {
    document.title = '3 preguntas que casi ningún dueño se hace — Oscar Rodriguez';
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');
    const setMetaTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setMetaTag('og:title', '3 preguntas que casi ningún dueño se hace');
    setMetaTag('og:description', 'Una reflexión guiada para articular el propósito que sostiene tu negocio — el que casi nunca aparece en los planes operativos.');
  }, []);
  useEffect(() => {
    if (currentScreen === 'results' && answers.includes(-1)) {
      setCurrentScreen('entry');
    }
  }, [currentScreen, answers]);
  const handleAnswer = (value: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = value;
    setAnswers(newAnswers);
  };
  const handleNext = () => {
    if (currentQuestionIndex < 2) {
      setCurrentQuestionIndex(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      setCurrentScreen('results');
      window.scrollTo(0, 0);
    }
  };
  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };
  const renderEntry = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen flex flex-col items-center justify-center px-6 text-center py-12" data-id="element-288">
      <img src="https://cdn.magicpatterns.com/uploads/8iq9FdZPWhhg1moFGMfStg/Gemini_Generated_Image_vohteevohteevoht_(1)-Photoroom.png" alt="OR Logo" className="h-16 mb-8" data-id="element-289" />
      <p className="text-gold text-sm font-bold tracking-widest uppercase mb-6" data-id="element-290">
        GUÍA DE PROPÓSITO
      </p>
      <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6 leading-tight max-w-4xl" data-id="element-291">
        3 preguntas que casi ningún dueño se hace.
      </h1>
      <h2 className="text-xl md:text-2xl font-serif text-gold mb-10 max-w-2xl leading-relaxed" data-id="element-292">
        Una reflexión guiada para articular el propósito que sostiene tu negocio
        — el que casi nunca aparece en los planes operativos.
      </h2>

      {/* Intro */}
      <div className="max-w-[680px] text-left space-y-5 text-white/90 font-serif text-lg leading-relaxed mb-12" data-id="element-293">
        <p data-id="element-294">
          Si llevas años operando un negocio, probablemente has hecho preguntas
          tácticas mil veces.
        </p>
        <p className="text-gray-light italic" data-id="element-295">
          ¿Cómo escalo? ¿Cómo delego? ¿Qué herramienta uso? ¿Cómo organizo mejor
          mi operación?
        </p>
        <p data-id="element-296">
          Esas preguntas tienen respuestas técnicas. Hay frameworks,
          herramientas, mentores y consultores que las trabajan.
        </p>
        <p data-id="element-297">
          Pero hay otras tres preguntas que casi ningún dueño se hace
          explícitamente. Y son las que determinan, en silencio, todas las
          decisiones operativas.
        </p>
        <p className="text-gold font-bold not-italic" data-id="element-298">
          ¿Por qué empezaste? ¿Para qué quieres seguir? ¿Qué quieres dejar?
        </p>
        <p data-id="element-299">
          Esta guía es un espacio para hacerte esas tres preguntas con calma. No
          hay respuestas correctas ni incorrectas. No hay puntaje. Solo claridad
          — la que aparezca, la que sea.
        </p>
      </div>

      <button onClick={() => setCurrentScreen('questions')} className="bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-10 py-5 rounded transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(201,168,76,0.3)]" data-id="element-300">
        Comenzar reflexión →
      </button>
      <p className="text-sm text-gray-light/60 mt-6" data-id="element-301">
        3 preguntas. Lectura y reflexión de 8-10 minutos. Sin formularios. Sin
        descargas.
      </p>
    </motion.div>;
  const renderQuestion = () => {
    const q = questions[currentQuestionIndex];
    const progress = (currentQuestionIndex + 1) / 3 * 100;
    const hasAnswered = answers[currentQuestionIndex] !== -1;
    return <motion.div key={currentQuestionIndex} initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="min-h-screen flex flex-col px-6 py-12 max-w-3xl mx-auto w-full" data-id="element-302">
        {/* Progress Bar */}
        <div className="w-full mb-16" data-id="element-303">
          <p className="text-xs text-gray-light mb-2 text-right uppercase tracking-wider" data-id="element-304">
            Pregunta {currentQuestionIndex + 1} de 3
          </p>
          <div className="w-full h-1 bg-navy-dark rounded-full overflow-hidden" data-id="element-305">
            <div className="h-full bg-gold transition-all duration-500" style={{
            width: `${progress}%`
          }} data-id="element-306" />
          </div>
        </div>

        <div className="flex-grow flex flex-col" data-id="element-307">
          <div className="mb-12" data-id="element-308">
            <span className="block text-7xl md:text-8xl font-serif font-bold text-gold mb-6 opacity-90" data-id="element-309">
              {String(q.id).padStart(2, '0')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white leading-tight" data-id="element-310">
              {q.title}
            </h2>
          </div>

          {/* Intro paragraphs */}
          <div className="space-y-6 mb-12 max-w-[700px]" data-id="element-311">
            {q.intro.map((paragraph, idx) => <p key={idx} className="text-lg text-white/90 font-serif leading-relaxed" data-id="element-312">
                {paragraph}
              </p>)}
          </div>

          {/* Sub-questions */}
          <div className="mb-12 max-w-[700px] border-l-2 border-gold/60 pl-6" data-id="element-313">
            <p className="text-sm uppercase tracking-widest text-gold font-bold mb-5" data-id="element-314">
              Para reflexionar
            </p>
            <ul className="space-y-4" data-id="element-315">
              {q.subQuestions.map((sq, idx) => <li key={idx} className="text-white/85 font-serif text-lg leading-relaxed" data-id="element-316">
                  {sq}
                </li>)}
            </ul>
          </div>

          {/* Evaluation Box */}
          <div className="bg-[#252540] border border-gold/30 rounded-xl p-8 mb-12" data-id="element-317">
            <p className="text-xl font-serif font-bold text-white mb-8 text-center" data-id="element-318">
              {q.evaluationQuestion}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-319">
              {q.options.map((opt, i) => {
              const isSelected = answers[currentQuestionIndex] === opt.value;
              return <button key={i} onClick={() => handleAnswer(opt.value)} className={`flex-1 py-4 px-4 rounded border transition-all duration-200 font-medium ${isSelected ? 'bg-gold border-gold text-navy' : 'bg-navy border-gold text-white hover:bg-[#2d2d4d]'}`} data-id="element-320">
                    {opt.text}
                  </button>;
            })}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center pt-6 border-t border-white/10" data-id="element-321">
          {currentQuestionIndex > 0 ? <button onClick={handlePrev} className="text-gray-light hover:text-white transition-colors px-4 py-2" data-id="element-322">
              ← Anterior
            </button> : <div data-id="element-323"></div>}
          <button onClick={handleNext} disabled={!hasAnswered} className={`font-bold px-8 py-3 rounded transition-all ${hasAnswered ? 'bg-gold text-navy hover:bg-yellow-500' : 'bg-navy-dark text-gray-light/30 cursor-not-allowed border border-white/10'}`} data-id="element-324">
            {currentQuestionIndex === 2 ? 'Ver síntesis →' : 'Siguiente →'}
          </button>
        </div>
      </motion.div>;
  };
  const renderResults = () => {
    // Highest clarity: highest score, if tie take FIRST (per spec on lowest; mirroring sensible default for highest)
    let maxScore = -1;
    let highestIndex = 0;
    answers.forEach((score, index) => {
      if (score > maxScore) {
        maxScore = score;
        highestIndex = index;
      }
    });
    // Lowest clarity: lowest score, if tie take first in order
    let minScore = 3;
    let lowestIndex = 0;
    answers.forEach((score, index) => {
      if (score < minScore) {
        minScore = score;
        lowestIndex = index;
      }
    });
    const highestQ = questions[highestIndex];
    const lowestQ = questions[lowestIndex];
    const highestMsg = highestClarityMessages[highestQ.id];
    const lowestMsg = lowestClarityMessages[lowestQ.id];
    const whatsappMsg = whatsappMessages[lowestQ.id];
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMsg)}`;
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen py-20 px-6 max-w-3xl mx-auto" data-id="element-325">
        {/* Section A — Opening */}
        <div className="text-center mb-20" data-id="element-326">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-327">
            Tu mapa de claridad
          </h1>
          <p className="text-lg text-gray-light font-serif max-w-xl mx-auto leading-relaxed" data-id="element-328">
            No hay perfil que asignar. Hay claridad — la que ya tienes y la que
            aún está pendiente.
          </p>
        </div>

        {/* Section B — Highest clarity */}
        <div className="mb-16" data-id="element-329">
          <p className="text-sm uppercase tracking-widest text-gold font-bold mb-6" data-id="element-330">
            Donde tienes mayor claridad hoy:
          </p>
          <div className="flex items-baseline gap-4 mb-6" data-id="element-331">
            <span className="text-5xl md:text-6xl font-serif font-bold text-gold leading-none" data-id="element-332">
              {String(highestQ.id).padStart(2, '0')}
            </span>
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white leading-tight" data-id="element-333">
              {highestQ.shortTitle}
            </h2>
          </div>
          <p className="text-white font-serif text-lg leading-relaxed max-w-[700px]" data-id="element-334">
            {highestMsg}
          </p>
        </div>

        {/* Section C — Lowest clarity */}
        <div className="mb-20 bg-navy-dark p-8 md:p-10 rounded-xl border border-gold/20" data-id="element-335">
          <p className="text-sm uppercase tracking-widest text-gold font-bold mb-6" data-id="element-336">
            Donde hay más por trabajar:
          </p>
          <div className="flex items-baseline gap-4 mb-6" data-id="element-337">
            <span className="text-5xl md:text-6xl font-serif font-bold text-gold leading-none" data-id="element-338">
              {String(lowestQ.id).padStart(2, '0')}
            </span>
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white leading-tight" data-id="element-339">
              {lowestQ.shortTitle}
            </h2>
          </div>
          <p className="text-white font-serif text-lg leading-relaxed" data-id="element-340">
            {lowestMsg}
          </p>
        </div>

        {/* Section D — Closing note */}
        <div className="mb-20 text-center" data-id="element-341">
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-8" data-id="element-342">
            Una nota final:
          </h3>
          <div className="space-y-5 text-lg text-white/90 font-serif leading-relaxed max-w-[700px] mx-auto text-left" data-id="element-343">
            <p data-id="element-344">
              Construir un negocio es relativamente fácil hoy. Hay herramientas,
              frameworks, mentores, capital, ejemplos. Lo difícil — lo que casi
              nadie te enseña — es construir un negocio que valga la pena.
            </p>
            <p data-id="element-345">
              Y un negocio vale la pena cuando hay coherencia entre el por qué,
              el para qué, y el qué quieres dejar.
            </p>
            <p data-id="element-346">
              Si una de esas tres está clara y las otras dos no, todavía hay
              algo que articular. Eso no es una falla. Es la siguiente
              conversación que vale la pena tener — contigo mismo, con tu
              pareja, con quien sea que esté a tu lado en esto.
            </p>
            <p className="text-gold" data-id="element-347">
              Y si quieres tenerla conmigo, estoy disponible.
            </p>
          </div>
        </div>

        {/* Section E — WhatsApp CTA */}
        <div className="text-center border border-gold p-8 md:p-12 rounded-xl mb-20" data-id="element-348">
          <h2 className="text-3xl font-serif font-bold text-gold mb-4" data-id="element-349">
            ¿Quieres conversar sobre lo que está debajo?
          </h2>
          <p className="text-white font-sans text-lg mb-10 max-w-xl mx-auto" data-id="element-350">
            Te respondo personalmente. Sin formularios. Sin secuencias
            automáticas. Una conversación directa sobre lo que pensaste mientras
            hacías esta guía.
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-block bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-10 py-5 rounded transition-all duration-300 hover:scale-105 shadow-xl w-full sm:w-auto" data-id="element-351">
            Hablemos por WhatsApp →
          </a>
          <p className="text-sm text-gray-light mt-6" data-id="element-352">
            Para dueños de negocio con operación activa.
          </p>
        </div>

        {/* Section F — Footer */}
        <div className="text-center border-t border-white/10 pt-12 pb-8" data-id="element-353">
          <img src="https://cdn.magicpatterns.com/uploads/8iq9FdZPWhhg1moFGMfStg/Gemini_Generated_Image_vohteevohteevoht_(1)-Photoroom.png" alt="OR Logo" className="h-10 mx-auto mb-4 opacity-50" data-id="element-354" />
          <a href="https://instagram.com/oscarrodriguezm" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-yellow-500 text-sm font-serif block mb-6" data-id="element-355">
            @oscarrodriguezm
          </a>
          <p className="text-sm text-gray-light" data-id="element-356">
            © 2026 Oscar Rodriguez. Todos los derechos reservados.
          </p>
        </div>
      </motion.div>;
  };
  return <div className="min-h-screen bg-navy text-white font-sans selection:bg-gold/30 selection:text-white" data-id="element-357">
      <AnimatePresence mode="wait" data-id="element-358">
        {currentScreen === 'entry' && renderEntry()}
        {currentScreen === 'questions' && renderQuestion()}
        {currentScreen === 'results' && renderResults()}
      </AnimatePresence>
    </div>;
}